import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiX, FiShoppingBag, FiPackage } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { smartSearch } from '../services/productService';
import { generateBrandedProductSlug } from '../utils/slugUtils';

// Custom debounce function
const debounce = (func, delay) => {
    let timeoutId;
    const debouncedFunc = (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
    debouncedFunc.cancel = () => clearTimeout(timeoutId);
    return debouncedFunc;
};

const SearchDropdown = ({ 
    value, 
    onChange, 
    onSelect, 
    placeholder = "Search products...", 
    className = "",
    disabled = false 
}) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [searchResults, setSearchResults] = useState({ brands: [], products: [] });
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [selectedType, setSelectedType] = useState(null); // 'brand' or 'product'
    
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce(async (searchTerm) => {
            if (!searchTerm.trim()) {
                setSearchResults({ brands: [], products: [] });
                setIsOpen(false);
                return;
            }

            try {
                setLoading(true);
                const response = await smartSearch(searchTerm, 8);
                setSearchResults(response);
                setIsOpen(true);
            } catch (error) {
                console.error('Search error:', error);
                setSearchResults({ brands: [], products: [] });
            } finally {
                setLoading(false);
            }
        }, 300),
        []
    );

    // Handle input change
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        onChange(newValue);
        setSelectedIndex(-1);
        setSelectedType(null);
        debouncedSearch(newValue);
    };

    // Handle brand selection - navigate to All Products page with brand filter
    const handleBrandSelect = (brand) => {
        setIsOpen(false);
        setSelectedIndex(-1);
        setSelectedType(null);
        onChange(brand.name);
        navigate(`/products?brand=${encodeURIComponent(brand.name)}`);
        inputRef.current?.blur();
    };

    // Handle product selection - navigate directly to product detail page
    const handleProductSelect = (product) => {
        setIsOpen(false);
        setSelectedIndex(-1);
        setSelectedType(null);
        onChange(product.name);
        
        // Navigate directly to the product detail page
        const productSlug = generateBrandedProductSlug(product.brand, product.name);
        navigate(`/product/${productSlug}`);
        inputRef.current?.blur();
    };

    // Handle clear
    const handleClear = () => {
        onChange('');
        setSearchResults({ brands: [], products: [] });
        setIsOpen(false);
        setSelectedIndex(-1);
        setSelectedType(null);
        inputRef.current?.focus();
    };

    // Get all search results as a flat array for keyboard navigation
    const getAllResults = () => {
        const results = [];
        searchResults.brands.forEach((brand, index) => {
            results.push({ ...brand, type: 'brand', index });
        });
        searchResults.products.forEach((product, index) => {
            results.push({ ...product, type: 'product', index });
        });
        return results;
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        const allResults = getAllResults();
        if (!isOpen || allResults.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < allResults.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev > 0 ? prev - 1 : allResults.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0) {
                    const selectedItem = allResults[selectedIndex];
                    if (selectedItem.type === 'brand') {
                        handleBrandSelect(selectedItem);
                    } else {
                        handleProductSelect(selectedItem);
                    }
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setSelectedIndex(-1);
                setSelectedType(null);
                inputRef.current?.blur();
                break;
        }
    };

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Clean up debounced function
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (value && (searchResults.brands.length > 0 || searchResults.products.length > 0)) {
                            setIsOpen(true);
                        }
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`input pl-10 pr-10 w-full ${
                        disabled ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                />
                {value && !disabled && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600"
                    >
                        <FiX className="h-5 w-5 text-gray-400" />
                    </button>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-[#f8f8f8] border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
                    {loading ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-600 mr-2"></div>
                            Searching...
                        </div>
                    ) : (searchResults.brands.length > 0 || searchResults.products.length > 0) ? (
                        <div className="py-1">
                            {/* Brands Section */}
                            {searchResults.brands.length > 0 && (
                                <div className="px-3 py-2">
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center">
                                        <FiShoppingBag className="h-3 w-3 mr-1" />
                                        Brands
                                    </div>
                                    {searchResults.brands.map((brand, index) => {
                                        const globalIndex = index;
                                        return (
                                            <button
                                                key={`brand-${brand.slug}`}
                                                type="button"
                                                onClick={() => handleBrandSelect(brand)}
                                                className={`w-full px-3 py-2 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 flex items-center space-x-3 rounded-md ${
                                                    globalIndex === selectedIndex ? 'bg-gray-50' : ''
                                                }`}
                                            >
                                                <div className="flex-shrink-0">
                                                    <div className="h-8 w-8 rounded-md bg-gray-200 flex items-center justify-center">
                                                        <FiShoppingBag className="h-4 w-4 text-gray-500" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {brand.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {brand.productCount} product{brand.productCount !== 1 ? 's' : ''}
                                                    </p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Products Section */}
                            {searchResults.products.length > 0 && (
                                <div className="px-3 py-2">
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center">
                                        <FiPackage className="h-3 w-3 mr-1" />
                                        Products
                                    </div>
                                    {searchResults.products.map((product, index) => {
                                        const globalIndex = searchResults.brands.length + index;
                                        return (
                                            <button
                                                key={product._id}
                                                type="button"
                                                onClick={() => handleProductSelect(product)}
                                                className={`w-full px-3 py-2 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 flex items-center space-x-3 rounded-md ${
                                                    globalIndex === selectedIndex ? 'bg-gray-50' : ''
                                                }`}
                                            >
                                                <div className="flex-shrink-0">
                                                    <img
                                                        src={product.imageUrl || '/placeholder.png'}
                                                        alt={product.name}
                                                        className="h-8 w-8 rounded-md object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {product.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {product.brand}
                                                    </p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ) : value.trim() && !loading ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            No results found for "{value.trim()}"
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default SearchDropdown;
